import { shouldSkipObject } from '@/threejs/objectManager';
import * as THREE from 'three';
import type { Projector } from './projector';

type EdgeUserData = {
  projectorOverlayIgnore?: boolean;
  projectorEdgeIgnore?: boolean;
  projectorEdgeState?: {
    transparent: boolean;
    depthWrite: boolean;
    depthTest: boolean;
    blending: THREE.Blending;
    premultipliedAlpha: boolean;
  };
  projectorFrustumOverlay?: {
    applied?: boolean;
  };
  projectorFrustumEdge?: {
    applied?: boolean;
    uniforms?: THREE.Shader['uniforms'];
  };
};

export type ProjectorFrustumEdgeOptions = {
  maxProjectors?: number;
  color?: THREE.ColorRepresentation;
  edge?: number;
  depthSize?: number;
  depthBias?: number;
  facingThreshold?: number;
  useTextureArray?: boolean;
  helperLinesEnabled?: boolean;
};

type ProjectorHelperLine = {
  line: THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  positions: Float32Array;
  material: THREE.LineBasicMaterial;
};

export class ProjectorFrustumEdge {
  private readonly maxProjectors: number;
  private readonly color: THREE.Color;
  private edge: number;
  private readonly materials = new Set<THREE.Material>();
  private readonly matrices: THREE.Matrix4[];
  private readonly projectorPositions: THREE.Vector3[];
  private readonly projectorColors: THREE.Color[];
  private depthTargets: THREE.WebGLRenderTarget[] = [];
  private depthTextures: THREE.DepthTexture[] = [];
  private readonly depthArrayTarget?: THREE.WebGLArrayRenderTarget;
  private readonly depthMaterial: THREE.MeshDepthMaterial;
  private readonly depthSize: number;
  private readonly depthBias: number;
  private readonly useTextureArray: boolean;
  private readonly overrideMaterial: THREE.MeshStandardMaterial;
  private facingThreshold: number;
  private edgeOnly = false;
  private readonly projectorHelperLines: ProjectorHelperLine[] = [];
  private helperLinesEnabled: boolean;
  private readonly helperRaycaster: THREE.Raycaster;
  private readonly helperOrigin = new THREE.Vector3();
  private readonly helperEnd = new THREE.Vector3();
  private readonly helperCorners = [
    new THREE.Vector2(-1, -1),
    new THREE.Vector2(1, -1),
    new THREE.Vector2(1, 1),
    new THREE.Vector2(-1, 1),
  ];

  constructor(options: ProjectorFrustumEdgeOptions = {}) {
    this.maxProjectors = Math.max(1, options.maxProjectors ?? 16);
    this.color = new THREE.Color(options.color ?? 0x77e4ff);
    this.edge = options.edge ?? 1.5;
    this.depthSize = options.depthSize ?? 1024;
    this.depthBias = options.depthBias ?? 0.0025;
    this.facingThreshold = options.facingThreshold ?? -1;
    this.useTextureArray = options.useTextureArray ?? false;
    this.helperLinesEnabled = options.helperLinesEnabled ?? true;
    this.matrices = Array.from({ length: this.maxProjectors }, () => new THREE.Matrix4());
    this.projectorPositions = Array.from({ length: this.maxProjectors }, () => new THREE.Vector3());
    this.projectorColors = Array.from({ length: this.maxProjectors }, () => this.color.clone());
    this.helperRaycaster = new THREE.Raycaster();
    this.helperRaycaster.layers.set(0);
    this.depthMaterial = new THREE.MeshDepthMaterial();
    this.depthMaterial.blending = THREE.NoBlending;

    if (this.useTextureArray) {
      this.depthMaterial.depthPacking = THREE.RGBADepthPacking;
      const target = new THREE.WebGLArrayRenderTarget(this.depthSize, this.depthSize, this.maxProjectors, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        depthBuffer: true,
        stencilBuffer: false,
      });
      target.texture.minFilter = THREE.NearestFilter;
      target.texture.magFilter = THREE.NearestFilter;
      target.texture.generateMipmaps = false;
      target.texture.wrapS = THREE.ClampToEdgeWrapping;
      target.texture.wrapT = THREE.ClampToEdgeWrapping;
      this.depthArrayTarget = target;
    } else {
      this.depthTargets = Array.from({ length: this.maxProjectors }, () => {
        const target = new THREE.WebGLRenderTarget(this.depthSize, this.depthSize);
        target.texture.minFilter = THREE.NearestFilter;
        target.texture.magFilter = THREE.NearestFilter;
        target.texture.generateMipmaps = false;
        target.depthTexture = new THREE.DepthTexture(this.depthSize, this.depthSize);
        target.depthTexture.format = THREE.DepthFormat;
        target.depthTexture.type = THREE.UnsignedIntType;
        target.depthTexture.minFilter = THREE.NearestFilter;
        target.depthTexture.magFilter = THREE.NearestFilter;
        return target;
      });
      this.depthTextures = this.depthTargets.map((target) => target.depthTexture);
    }

    this.overrideMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.6,
      metalness: 0.0,
    });
    this.overrideMaterial.depthTest = false;
    this.overrideMaterial.depthWrite = false;
    this.decorateMaterial(this.overrideMaterial);
  }

  applyTo(root: THREE.Object3D) {
    root.traverse((object) => {
      if (!('isMesh' in object) || !(object as THREE.Mesh).isMesh) {
        return;
      }

      const mesh = object as THREE.Mesh;
      if (
        (mesh.userData as EdgeUserData).projectorOverlayIgnore ||
        (mesh.userData as EdgeUserData).projectorEdgeIgnore
      ) {
        return;
      }

      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((material) => this.decorateMaterial(material));
      } else if (mesh.material) {
        this.decorateMaterial(mesh.material);
      }
    });
  }

  update(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    projectors: Projector[],
    options: { edgeOnly?: boolean } = {}
  ) {
    const count = Math.min(projectors.length, this.maxProjectors);
    this.edgeOnly = options.edgeOnly ?? false;
    if (count === 0) {
      this.applyUniforms(0);
      return;
    }

    const previousRenderTarget = renderer.getRenderTarget();
    const previousClearColor = new THREE.Color();
    renderer.getClearColor(previousClearColor);
    const previousClearAlpha = renderer.getClearAlpha();
    const previousAutoClear = renderer.autoClear;
    const previousOverrideMaterial = scene.overrideMaterial;

    renderer.autoClear = true;
    scene.overrideMaterial = this.depthMaterial;
    renderer.setClearColor(0x000000, 1);

    const hiddenObjects: THREE.Object3D[] = [];
    scene.traverse((object: THREE.Object3D) => {
      if (object.visible && shouldSkipObject(object)) {
        object.visible = false;
        hiddenObjects.push(object);
      }
    });

    for (let i = 0; i < count; i += 1) {
      const projector = projectors[i];
      projector.updateMatrixWorld(true);
      projector.camera.updateMatrixWorld(true);
      this.matrices[i].multiplyMatrices(projector.camera.projectionMatrix, projector.camera.matrixWorldInverse);
      this.projectorPositions[i].setFromMatrixPosition(projector.camera.matrixWorld);
      const edgeColor = (projector.userData as { edgeColor?: THREE.Color }).edgeColor;
      if (edgeColor) {
        this.projectorColors[i].copy(edgeColor);
      } else {
        this.projectorColors[i].copy(this.color);
      }

      if (this.useTextureArray) {
        if (!this.depthArrayTarget) {
          continue;
        }
        renderer.setRenderTarget(this.depthArrayTarget, i);
      } else {
        renderer.setRenderTarget(this.depthTargets[i]);
      }
      renderer.clear();
      renderer.render(scene, projector.camera);
    }
    for (const object of hiddenObjects) {
      object.visible = true;
    }

    renderer.setRenderTarget(previousRenderTarget);
    renderer.setClearColor(previousClearColor, previousClearAlpha);
    renderer.autoClear = previousAutoClear;
    scene.overrideMaterial = previousOverrideMaterial;

    this.applyUniforms(count);
  }

  setEdgeThickness(edge: number) {
    this.edge = Math.max(0.001, edge);
  }

  setFacingThreshold(threshold: number) {
    this.facingThreshold = Math.max(-1, Math.min(1, threshold));
  }

  clearEdges() {
    this.edgeOnly = false;
    this.applyUniforms(0);
  }

  setHelperLinesEnabled(enabled: boolean) {
    this.helperLinesEnabled = enabled;
    for (const helper of this.projectorHelperLines) {
      helper.line.visible = enabled;
    }
  }

  createHelperLines(projector: Projector, gizmoScene: THREE.Scene, gizmoLayer: number) {
    const positions = new Float32Array(4 * 2 * 3);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const edgeColor = (projector.userData as { edgeColor?: THREE.Color }).edgeColor;
    const material = new THREE.LineBasicMaterial({
      color: edgeColor ?? this.color,
      transparent: true,
      opacity: 1,
    });
    material.depthTest = false;
    material.depthWrite = false;
    const line = new THREE.LineSegments(geometry, material);
    line.frustumCulled = false;
    line.renderOrder = 999;
    line.layers.set(gizmoLayer);
    line.visible = this.helperLinesEnabled;
    line.userData.projectorOverlayIgnore = true;
    gizmoScene.add(line);
    const helper = { line, positions, material };
    this.projectorHelperLines.push(helper);
    return helper;
  }

  updateHelperLines(projectors: Projector[], scene: THREE.Scene, projectorTarget: THREE.Vector3) {
    if (!this.helperLinesEnabled) {
      return;
    }

    for (let i = 0; i < projectors.length; i += 1) {
      const projector = projectors[i];
      const helper = this.projectorHelperLines[i];
      if (!helper) {
        continue;
      }
      helper.line.visible = true;
      projector.updateMatrixWorld(true);
      projector.camera.updateMatrixWorld(true);
      projector.camera.getWorldPosition(this.helperOrigin);
      const targetDistance = Math.max(this.helperOrigin.distanceTo(projectorTarget), 0.05);
      const edgeColor = (projector.userData as { edgeColor?: THREE.Color }).edgeColor;
      helper.material.color.copy(edgeColor ?? this.color);

      for (let cornerIndex = 0; cornerIndex < this.helperCorners.length; cornerIndex += 1) {
        const corner = this.helperCorners[cornerIndex];
        this.helperRaycaster.setFromCamera({ x: corner.x, y: corner.y }, projector.camera);
        const hit = this.helperRaycaster.intersectObjects(scene.children, true)[0];
        if (hit) {
          this.helperEnd.copy(hit.point);
        } else {
          this.helperEnd
            .copy(this.helperRaycaster.ray.direction)
            .multiplyScalar(targetDistance)
            .add(this.helperRaycaster.ray.origin);
        }
        const offset = cornerIndex * 6;
        helper.positions[offset] = this.helperOrigin.x;
        helper.positions[offset + 1] = this.helperOrigin.y;
        helper.positions[offset + 2] = this.helperOrigin.z;
        helper.positions[offset + 3] = this.helperEnd.x;
        helper.positions[offset + 4] = this.helperEnd.y;
        helper.positions[offset + 5] = this.helperEnd.z;
      }

      helper.line.geometry.attributes.position.needsUpdate = true;
    }
  }

  getBatchSize() {
    return this.maxProjectors;
  }

  getOverrideMaterial() {
    return this.overrideMaterial;
  }

  beginEdgePass() {
    for (const material of this.materials) {
      const userData = material.userData as EdgeUserData;
      if (!userData.projectorEdgeState) {
        userData.projectorEdgeState = {
          transparent: material.transparent,
          depthWrite: material.depthWrite,
          depthTest: material.depthTest,
          blending: material.blending,
          premultipliedAlpha: material.premultipliedAlpha,
        };
      }

      material.transparent = true;
      material.depthWrite = false;
      material.depthTest = true;
      material.blending = THREE.AdditiveBlending;
      material.premultipliedAlpha = false;
    }
  }

  endEdgePass() {
    for (const material of this.materials) {
      const userData = material.userData as EdgeUserData;
      const state = userData.projectorEdgeState;
      if (!state) {
        continue;
      }

      material.transparent = state.transparent;
      material.depthWrite = state.depthWrite;
      material.depthTest = state.depthTest;
      material.blending = state.blending;
      material.premultipliedAlpha = state.premultipliedAlpha;
    }
  }

  private decorateMaterial(material: THREE.Material) {
    if (!('isMeshStandardMaterial' in material) && !('isMeshPhysicalMaterial' in material)) {
      return;
    }

    const userData = material.userData as EdgeUserData;
    if (userData.projectorFrustumOverlay?.applied) {
      return;
    }
    if (userData.projectorFrustumEdge?.applied) {
      return;
    }

    const previous = material.onBeforeCompile;
    material.onBeforeCompile = (shader) => {
      previous?.(shader);

      shader.uniforms.uProjectorEdgeCount = { value: 0 };
      shader.uniforms.uProjectorEdgeMatrix = {
        value: this.matrices.map((m) => m.clone()),
      };
      shader.uniforms.uProjectorEdgeColor = {
        value: this.projectorColors.map((color) => color.clone()),
      };
      shader.uniforms.uProjectorEdgeThickness = { value: this.edge };
      shader.uniforms.uProjectorEdgePosition = {
        value: this.projectorPositions.map((p) => p.clone()),
      };
      shader.uniforms.uProjectorEdgeOnly = { value: 0 };
      shader.uniforms.uProjectorEdgeBias = { value: this.depthBias };
      shader.uniforms.uProjectorEdgeFacingThreshold = {
        value: this.facingThreshold,
      };
      if (this.useTextureArray) {
        shader.uniforms.uProjectorEdgeDepthArray = {
          value: this.depthArrayTarget?.texture ?? null,
        };
      } else {
        for (let i = 0; i < this.depthTextures.length; i += 1) {
          shader.uniforms[`uProjectorEdgeDepth${i}`] = {
            value: this.depthTextures[i],
          };
        }
      }

      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\n' + 'varying vec3 vProjectorEdgeWorldPosition;')
        .replace(
          '#include <project_vertex>',
          [
            '#include <project_vertex>',
            'vec4 projectorEdgeWorldPosition = vec4( transformed, 1.0 );',
            '#ifdef USE_BATCHING',
            '  projectorEdgeWorldPosition = batchingMatrix * projectorEdgeWorldPosition;',
            '#endif',
            '#ifdef USE_INSTANCING',
            '  projectorEdgeWorldPosition = instanceMatrix * projectorEdgeWorldPosition;',
            '#endif',
            'projectorEdgeWorldPosition = modelMatrix * projectorEdgeWorldPosition;',
            'vProjectorEdgeWorldPosition = projectorEdgeWorldPosition.xyz;',
          ].join('\n')
        );

      const fragmentHeaderLines = [
        '#include <common>',
        `#define EDGE_MAX_PROJECTORS ${this.maxProjectors}`,
        'varying vec3 vProjectorEdgeWorldPosition;',
        'uniform int uProjectorEdgeCount;',
        'uniform mat4 uProjectorEdgeMatrix[EDGE_MAX_PROJECTORS];',
        'uniform vec3 uProjectorEdgeColor[EDGE_MAX_PROJECTORS];',
        'uniform float uProjectorEdgeThickness;',
        'uniform vec3 uProjectorEdgePosition[EDGE_MAX_PROJECTORS];',
        'uniform float uProjectorEdgeOnly;',
        'uniform float uProjectorEdgeBias;',
        'uniform float uProjectorEdgeFacingThreshold;',
      ];

      if (this.useTextureArray) {
        fragmentHeaderLines.push('uniform sampler2DArray uProjectorEdgeDepthArray;');
      } else {
        fragmentHeaderLines.push(
          ...Array.from({ length: this.maxProjectors }, (_, i) => `uniform sampler2D uProjectorEdgeDepth${i};`)
        );
      }

      if (this.useTextureArray) {
        fragmentHeaderLines.push(
          'float projectorEdgeUnpackRGBAToDepth(const in vec4 v) {',
          '  const vec4 UnpackFactors4 = vec4(',
          '    255.0 / 256.0,',
          '    (255.0 / 256.0) / 256.0,',
          '    (255.0 / 256.0) / (256.0 * 256.0),',
          '    1.0 / (256.0 * 256.0 * 256.0)',
          '  );',
          '  return dot(v, UnpackFactors4);',
          '}'
        );
      }

      const fragmentHeader = fragmentHeaderLines.join('\n');

      let edgeBlocks = '';
      if (this.useTextureArray) {
        edgeBlocks = [
          'float projectorEdgeLine = 0.0;',
          'vec3 projectorEdgeColor = uProjectorEdgeColor[0];',
          'vec3 edgeViewPos = -vViewPosition;',
          'for (int i = 0; i < EDGE_MAX_PROJECTORS; i++) {',
          '  if (i >= uProjectorEdgeCount) { break; }',
          '  vec4 clip = uProjectorEdgeMatrix[i] * vec4(vProjectorEdgeWorldPosition, 1.0);',
          '  if (clip.w <= 0.0) { continue; }',
          '  vec3 ndc = clip.xyz / clip.w;',
          '  if (ndc.x < -1.0 || ndc.x > 1.0 || ndc.y < -1.0 || ndc.y > 1.0 || ndc.z < -1.0 || ndc.z > 1.0) { continue; }',
          '  vec2 uv = ndc.xy * 0.5 + 0.5;',
          '  float depthSample = projectorEdgeUnpackRGBAToDepth(texture(uProjectorEdgeDepthArray, vec3(uv, float(i))));',
          '  float fragDepth = ndc.z * 0.5 + 0.5;',
          '  if (fragDepth - uProjectorEdgeBias > depthSample) { continue; }',
          '  if (uProjectorEdgeFacingThreshold > -0.999) {',
          '    vec3 projectorViewPos = (viewMatrix * vec4(uProjectorEdgePosition[i], 1.0)).xyz;',
          '    vec3 projectorDir = normalize(projectorViewPos - edgeViewPos);',
          '    if (dot(normal, projectorDir) <= uProjectorEdgeFacingThreshold) { continue; }',
          '  }',
          '  float edgeX = min(abs(ndc.x - 1.0), abs(ndc.x + 1.0));',
          '  float edgeY = min(abs(ndc.y - 1.0), abs(ndc.y + 1.0));',
          '  float px = max(fwidth(ndc.x), 1e-6);',
          '  float py = max(fwidth(ndc.y), 1e-6);',
          '  float edgePixels = min(edgeX / px, edgeY / py);',
          '  float line = 1.0 - smoothstep(uProjectorEdgeThickness, uProjectorEdgeThickness + 1.0, edgePixels);',
          '  if (line > projectorEdgeLine) {',
          '    projectorEdgeLine = line;',
          '    projectorEdgeColor = uProjectorEdgeColor[i];',
          '  }',
          '}',
          'float edgeAlpha = projectorEdgeLine;',
          'if (uProjectorEdgeOnly > 0.5) {',
          '  if (edgeAlpha < 0.001) { discard; }',
          '  outgoingLight = projectorEdgeColor * edgeAlpha;',
          '  diffuseColor.a = edgeAlpha;',
          '}',
        ].join('\n');
      } else {
        edgeBlocks = [
          'float projectorEdgeLine = 0.0;',
          'vec3 projectorEdgeColor = uProjectorEdgeColor[0];',
          'vec3 edgeViewPos = -vViewPosition;',
          ...Array.from({ length: this.maxProjectors }, (_, i) => {
            return [
              `if (uProjectorEdgeCount > ${i}) {`,
              `  vec4 clip${i} = uProjectorEdgeMatrix[${i}] * vec4(vProjectorEdgeWorldPosition, 1.0);`,
              `  if (clip${i}.w > 0.0) {`,
              `    vec3 ndc${i} = clip${i}.xyz / clip${i}.w;`,
              `    if (ndc${i}.x >= -1.0 && ndc${i}.x <= 1.0 && ndc${i}.y >= -1.0 && ndc${i}.y <= 1.0 && ndc${i}.z >= -1.0 && ndc${i}.z <= 1.0) {`,
              `      vec2 uv${i} = ndc${i}.xy * 0.5 + 0.5;`,
              `      float depthSample${i} = texture2D(uProjectorEdgeDepth${i}, uv${i}).x;`,
              `      float fragDepth${i} = ndc${i}.z * 0.5 + 0.5;`,
              `      if (fragDepth${i} - uProjectorEdgeBias <= depthSample${i}) {`,
              `        float facing${i} = dot(normal, normalize((viewMatrix * vec4(uProjectorEdgePosition[${i}], 1.0)).xyz - edgeViewPos));`,
              `        if (uProjectorEdgeFacingThreshold <= -0.999 || facing${i} > uProjectorEdgeFacingThreshold) {`,
              `          float edgeX${i} = min(abs(ndc${i}.x - 1.0), abs(ndc${i}.x + 1.0));`,
              `          float edgeY${i} = min(abs(ndc${i}.y - 1.0), abs(ndc${i}.y + 1.0));`,
              `          float px${i} = max(fwidth(ndc${i}.x), 1e-6);`,
              `          float py${i} = max(fwidth(ndc${i}.y), 1e-6);`,
              `          float edgePixels${i} = min(edgeX${i} / px${i}, edgeY${i} / py${i});`,
              `          float line${i} = 1.0 - smoothstep(uProjectorEdgeThickness, uProjectorEdgeThickness + 1.0, edgePixels${i});`,
              `          if (line${i} > projectorEdgeLine) {`,
              `            projectorEdgeLine = line${i};`,
              `            projectorEdgeColor = uProjectorEdgeColor[${i}];`,
              `          }`,
              '        }',
              '      }',
              '    }',
              '  }',
              '}',
            ].join('\n');
          }),
          'float edgeAlpha = projectorEdgeLine;',
          'if (uProjectorEdgeOnly > 0.5) {',
          '  if (edgeAlpha < 0.001) { discard; }',
          '  outgoingLight = projectorEdgeColor * edgeAlpha;',
          '  diffuseColor.a = edgeAlpha;',
          '}',
        ].join('\n');
      }

      const withHeader = shader.fragmentShader.replace('#include <common>', fragmentHeader);
      let fragment = withHeader.replace(
        '#include <output_fragment>',
        [edgeBlocks, '#include <output_fragment>'].join('\n')
      );

      if (fragment === withHeader) {
        fragment = withHeader.replace(
          '#include <opaque_fragment>',
          [edgeBlocks, '#include <opaque_fragment>'].join('\n')
        );
      }

      shader.fragmentShader = fragment;

      userData.projectorFrustumEdge = {
        applied: true,
        uniforms: shader.uniforms,
      };
    };

    material.needsUpdate = true;
    userData.projectorFrustumEdge = { applied: true };
    this.materials.add(material);
  }

  dispose() {
    for (const target of this.depthTargets) {
      target.dispose();
    }
    this.depthTargets = [];
    this.depthTextures = [];
    if (this.depthArrayTarget) {
      this.depthArrayTarget.dispose();
    }
    this.depthMaterial.dispose();
    this.overrideMaterial.dispose();
    for (const line of this.projectorHelperLines) {
      if (line.line.geometry) line.line.geometry.dispose();
      if (line.line.material) (line.line.material as THREE.Material).dispose();
    }
    this.projectorHelperLines.length = 0;
    this.materials.clear();
  }

  private applyUniforms(count: number) {
    for (const material of this.materials) {
      const data = (material.userData as EdgeUserData).projectorFrustumEdge;
      if (!data?.uniforms) {
        continue;
      }

      data.uniforms.uProjectorEdgeCount.value = count;
      const colorTarget = data.uniforms.uProjectorEdgeColor.value as THREE.Color[];
      data.uniforms.uProjectorEdgeThickness.value = this.edge;
      data.uniforms.uProjectorEdgeOnly.value = this.edgeOnly ? 1 : 0;
      data.uniforms.uProjectorEdgeBias.value = this.depthBias;
      data.uniforms.uProjectorEdgeFacingThreshold.value = this.facingThreshold;
      if (this.useTextureArray && data.uniforms.uProjectorEdgeDepthArray) {
        data.uniforms.uProjectorEdgeDepthArray.value = this.depthArrayTarget?.texture ?? null;
      }

      const target = data.uniforms.uProjectorEdgeMatrix.value as THREE.Matrix4[];
      for (let i = 0; i < count; i += 1) {
        target[i].copy(this.matrices[i]);
      }
      const positionTarget = data.uniforms.uProjectorEdgePosition.value as THREE.Vector3[];
      for (let i = 0; i < count; i += 1) {
        positionTarget[i].copy(this.projectorPositions[i]);
        colorTarget[i].copy(this.projectorColors[i]);
      }
    }
  }
}
